import { Box, Chip, Container, Paper, Stack } from "@mui/material";
import { useSwiper } from "components/swiper";
import { useTheme } from "contexts";
import { Custom, Vector } from "components";
import { IPotluck } from "interfaces";
import { motion } from "framer-motion";

type Props = {
  potluck: IPotluck;
};

export const Controller = ({ potluck }: Props) => {
  const { theme } = useTheme();
  const {
    controls,
    currentMenuIndex,
    currentCategory,
    handleGoBack,
    handleSwipe,
    isDone,
    removeItem,
    selectedItems,
  } = useSwiper(potluck);

  return (
    <Box
      flex={1}
      display="flex"
      bgcolor={potluck?.menu[currentMenuIndex].configuration.bgColor}
    >
      <Container sx={{ display: "flex", flex: 1, py: theme.spacing.xxxl }}>
        <Paper
          elevation={0}
          sx={{ flex: 1, backgroundColor: theme.color.white }}
        >
          {!isDone && currentCategory && (
            <Stack
              height={1}
              alignItems="center"
              py={theme.spacing.xl}
              justifyContent="center"
            >
              <Custom.Typography
                textAlign="center"
                size={theme.font.lg}
                weight={theme.font.bold}
              >
                {potluck.menu[currentMenuIndex].configuration.name}
              </Custom.Typography>

              <Box display="flex" flex={1} alignItems="center">
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  alignItems="center"
                  gap={theme.spacing.xxl}
                  justifyContent="center"
                  spacing={{ xs: 0, md: theme.spacing.xxxl }}
                >
                  <Box
                    sx={{ order: { xs: 1, md: 0 }, cursor: "pointer" }}
                    onClick={() => handleSwipe(false)}
                  >
                    <Vector.Icon name="swipe_left" size={96} />
                  </Box>
                  <Box
                    component={motion.div}
                    animate={controls}
                    initial={{ x: 0, opacity: 1 }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    onDragEnd={(_, info) => {
                      const swipeConfidenceThreshold = 100;
                      const offset = info.offset.x;
                      const velocity = info.velocity.x;

                      if (offset > swipeConfidenceThreshold || velocity > 500) {
                        handleSwipe(true); // swipe left
                      } else if (
                        offset < -swipeConfidenceThreshold ||
                        velocity < -500
                      ) {
                        handleSwipe(false); // swipe right
                      }
                    }}
                    order={{ xs: 0, md: 1 }}
                    width={{ xs: 1, md: "auto" }}
                    sx={{
                      cursor: "grab",
                    }}
                  >
                    <Box
                      sx={{
                        width: 300,
                        height: 400,
                        display: "flex",
                        margin: "0 auto",
                        p: theme.spacing.xl,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: theme.border.circle / 4,
                        backgroundColor:
                          potluck.menu[currentMenuIndex].configuration.bgColor,
                      }}
                    >
                      <Custom.Typography
                        align="center"
                        size={theme.font.xl}
                        weight={theme.font.bold}
                        color={
                          potluck.menu[currentMenuIndex].configuration.textColor
                        }
                      >
                        {currentCategory.name}
                      </Custom.Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{ order: { xs: 2, md: 2 }, cursor: "pointer" }}
                    onClick={() => handleSwipe(true)}
                  >
                    <Vector.Icon name="swipe_right" size={96} />
                  </Box>
                </Stack>
              </Box>

              <Stack
                direction="row"
                flexWrap="wrap"
                justifyContent="center"
                gap={theme.spacing.md}
                sx={{ p: theme.spacing.xl }}
              >
                {selectedItems.map((item, i) => (
                  <Chip
                    key={i}
                    label={item.name}
                    onDelete={() => removeItem(i)}
                  />
                ))}
              </Stack>
            </Stack>
          )}
          {isDone && (
            <Stack
              height={1}
              alignItems="center"
              py={theme.spacing.xl}
              justifyContent="center"
            >
              <Custom.DotLottie
                src="https://lottie.host/ae1f320b-26b5-4355-9eea-912914d02584/IgKBYUAFq9.lottie"
                autoplay
                onComplete={handleGoBack}
              />
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
};
